import React, { useState } from 'react';
import {
  HelpCircle,
  ChevronDown,
  Mail,
  MessageCircle,
  ExternalLink,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { BackButton } from '../../components/ui/BackButton';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';

const FAQ_ITEMS = [
  {
    q: 'How do I track my order?',
    a: 'After placing an order, you can track it from the order confirmation page or from your order history. You\'ll also receive push notifications with real-time delivery updates.',
  },
  {
    q: 'What is the return policy?',
    a: 'Each boutique sets its own return policy. You can find the return policy on the product page or in your order details. Most boutiques accept returns within 14 days of delivery.',
  },
  {
    q: 'How long does delivery take?',
    a: 'Delivery times vary by boutique and your location. Most orders within the city are delivered same-day or next-day. You\'ll see estimated delivery times at checkout.',
  },
  {
    q: 'Is my payment information secure?',
    a: 'Absolutely. All payments are processed through Stripe, a PCI-compliant payment processor. We never store your full card details on our servers.',
  },
  {
    q: 'How do I contact a boutique?',
    a: 'Visit the boutique\'s page and use the contact options there. You can also reach out through your order details if you have a question about a specific purchase.',
  },
  {
    q: 'Can I cancel or modify my order?',
    a: 'You can cancel an order before it\'s been confirmed by the boutique. Once confirmed, please contact the boutique directly for any modifications.',
  },
  {
    q: 'How do I report an issue with my order?',
    a: 'Go to your order details and tap "Report an Issue" or "File a Dispute." Our team will review your case and get back to you within 24 hours.',
  },
];

function FAQItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border-b border-gray-100 last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
      >
        <p className="font-medium text-heading text-sm pr-4">{q}</p>
        <ChevronDown className={cn('h-5 w-5 text-gray-400 flex-shrink-0 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="px-4 pb-4">
          <p className="text-sm text-body leading-relaxed">{a}</p>
        </div>
      )}
    </div>
  );
}

export function HelpPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="sticky top-0 z-40 bg-white border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center">
            <BackButton fallback="/settings" />
            <h1 className="ml-4 text-xl font-serif text-heading">Help Center</h1>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6 pb-24">
        {/* FAQ */}
        <div>
          <h2 className="text-sm font-medium text-body uppercase tracking-wider mb-3 px-1">
            Frequently Asked Questions
          </h2>
          <Card>
            <CardContent className="p-0">
              {FAQ_ITEMS.map((item, i) => (
                <FAQItem key={i} q={item.q} a={item.a} />
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Contact */}
        <div>
          <h2 className="text-sm font-medium text-body uppercase tracking-wider mb-3 px-1">
            Still Need Help?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Card>
              <CardContent className="p-4">
                <a href="mailto:support@fashapp.com" className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                    <Mail className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-heading text-sm">Email Support</p>
                    <p className="text-xs text-body">support@fashapp.com</p>
                  </div>
                </a>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center">
                    <MessageCircle className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-heading text-sm">Live Chat</p>
                    <p className="text-xs text-body">Coming soon</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
